import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    Avatar,
} from "@heroui/react";
import {
    LuEye,
    LuPencil,
    LuTrash,
    LuPlus,
    LuRefreshCw,
} from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ChangeStatusModal,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
    ImageLightbox,
    NoImageFallback,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatCurrency, findOptionTitle } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    name: "",
    category_id: "",
    sub_category_id: "",
    unit_id: "",
    status_id: "",
    price_min: "",
    price_max: "",
};

const ProductData = () => {
    const { hasPermission } = useUser();
    const [lightbox, setLightbox] = useState({ isOpen: false, images: [] });
    const [statusActiveModal, setStatusActiveModal] = useState({
        isOpen: false,
        item: null,
    });

    const table = useDataTable(
        "materials/data",
        "materials/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups (subCategories fetched dynamically in FilterData based on category)
    const { data: categories } = useFetchData("select/material-category");
    const { data: subCategories } = useFetchData(
        "select/material-sub-category",
    );
    const { data: units } = useFetchData("select/material-unit");
    const { data: activeStatuses } = useFetchData("select/active-status");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = {
        categories: categories || [],
        units: units || [],
        statuses: activeStatuses || [],
    };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        name: { label: "Nama" },
        category_id: {
            label: "Kategori",
            getValueLabel: (val) => findOptionTitle(categories, val, val),
        },
        sub_category_id: {
            label: "Sub Kategori",
            getValueLabel: (val) => findOptionTitle(subCategories, val, val),
        },
        unit_id: {
            label: "Satuan",
            getValueLabel: (val) => findOptionTitle(units, val, val),
        },
        status_id: {
            label: "Status",
            getValueLabel: (val) => findOptionTitle(activeStatuses, val, val),
        },
        price_min: {
            label: "Harga Min",
            getValueLabel: (val) => `Rp ${Number(val).toLocaleString("id-ID")}`,
        },
        price_max: {
            label: "Harga Max",
            getValueLabel: (val) => `Rp ${Number(val).toLocaleString("id-ID")}`,
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Produk"
                    description="Terjadi kesalahan saat memuat data produk. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Produk", skeletonWidth: "w-48" },
        { name: "Kategori", skeletonWidth: "w-32" },
        { name: "Sub Kategori", skeletonWidth: "w-32" },
        { name: "Satuan", skeletonWidth: "w-20" },
        { name: "Harga", skeletonWidth: "w-24", align: "end" },
        { name: "Status Keaktifan", skeletonWidth: "w-20" },
        { name: "Aksi", skeletonWidth: "w-20", align: "end" },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header - Title/Breadcrumbs left, Actions right */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Produk"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Produk" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("material.create") && (
                        <Button
                            as={Link}
                            to="/products/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Produk
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada produk yang cocok dengan filter."
                                    : "Belum ada data produk."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Produk" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="name">Produk</TableColumn>
                                <TableColumn key="category">
                                    Kategori
                                </TableColumn>
                                <TableColumn key="sub_category">
                                    Sub Kategori
                                </TableColumn>
                                <TableColumn key="unit">Satuan</TableColumn>
                                <TableColumn align="end" key="price">
                                    Harga
                                </TableColumn>
                                <TableColumn key="status">
                                    Status Keaktifan
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar
                                                    src={
                                                        item.images?.[0]
                                                            ?.url_600
                                                    }
                                                    size="sm"
                                                    radius="full"
                                                    showFallback
                                                    className={
                                                        item.images?.length > 0
                                                            ? "shrink-0 cursor-pointer"
                                                            : "shrink-0"
                                                    }
                                                    onClick={() =>
                                                        item.images?.length >
                                                            0 &&
                                                        setLightbox({
                                                            isOpen: true,
                                                            images: item.images,
                                                        })
                                                    }
                                                    fallback={
                                                        <NoImageFallback />
                                                    }
                                                />
                                                <div className="flex flex-col">
                                                    <ThemedText size="sm">
                                                        {item.name}
                                                    </ThemedText>
                                                    {item.description && (
                                                        <ThemedText
                                                            size="xs"
                                                            className="text-default-400 line-clamp-1"
                                                        >
                                                            {item.description}
                                                        </ThemedText>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.category?.name || "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.sub_category?.name ||
                                                        "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <ThemedText size="sm">
                                                    {item.default_price?.unit
                                                        ?.name || "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.default_price?.price
                                                    ? formatCurrency(
                                                          item.default_price
                                                              .price,
                                                      )
                                                    : "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.active_status
                                                        ?.badge_color
                                                }
                                                size="sm"
                                                className="font-medium"
                                                variant="flat"
                                            >
                                                {item.active_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Detail Produk">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/products/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("material.edit") && (
                                                    <Tooltip content="Edit Produk">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/products/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("material.edit") && (
                                                    <Tooltip content="Ubah Status Keaktifan">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() =>
                                                                setStatusActiveModal(
                                                                    {
                                                                        isOpen: true,
                                                                        item: item,
                                                                    },
                                                                )
                                                            }
                                                        >
                                                            <LuRefreshCw
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("material.delete") && (
                                                    <Tooltip content="Hapus Produk">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            color="danger"
                                                            variant="light"
                                                            onPress={() =>
                                                                table.deleteState.openDeleteModal(
                                                                    item.id,
                                                                )
                                                            }
                                                        >
                                                            <LuTrash size={14} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />

            {/* Image Lightbox */}
            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ isOpen: false, images: [] })}
                images={lightbox.images}
                urlKey="url_1000"
            />

            {/* Status Change Modal */}
            <ChangeStatusModal
                isOpen={statusActiveModal.isOpen}
                onClose={() =>
                    setStatusActiveModal({ isOpen: false, item: null })
                }
                item={statusActiveModal.item}
                itemName={statusActiveModal.item?.name}
                statusEndpoint="select/active-status"
                statusField="active_status_id"
                endpoint="materials/{id}/change-active-status"
                onSuccess={() => table.refetch()}
            />
        </div>
    );
};

export default ProductData;
